import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseEntityField, BaseInfoCompActionsSet, BaseInfoCompProps, BaseInfoCompSaveAction, FontIcon, useModeFormStyle, useModePanelStyle,
    InfoCompPanelHeader } from '@moderepo/mode-app-components';
import { Button, Checkbox, makeStyles, Paper, Theme } from '@material-ui/core';
import clsx from 'clsx';


const useStyle = makeStyles((theme: Theme) => {
    return {

        metricsTable: {
            width: '100%',
        },

        metricsGroup: {
            '&:not(:last-child)': {
                paddingBottom: theme.spacing(1),
            },
        },

        metricDefinitionId: {
            fontWeight   : 'bold',
            textAlign    : 'center',
            textTransform: 'capitalize',
            paddingBottom: theme.spacing(0.5),
        },

        metricRow: {
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
            borderBottom : `1px solid ${theme.palette.divider}`,
            marginBottom : theme.spacing(1),
            paddingBottom: theme.spacing(0.5),
            '&.hidden'   : {
                display: 'none',
            },
            '&.timestamp': {
                borderBottom: 'none',
                borderTop   : `1px solid ${theme.palette.divider}`,
                padding     : theme.spacing(1, 2),
                marginBottom: 0,
                fontWeight  : 'bold',
            },
        },

        metricNameCol: {
            textAlign: 'left',
            flex     : 1,
            color    : theme.palette.text.primary,
        },

        metricValueCol: {
            textAlign  : 'right',
            whiteSpace : 'nowrap',
            color      : theme.palette.text.secondary,
            paddingLeft: theme.spacing(1),

            '&.timestamp': {
                whiteSpace: 'nowrap',
            },
        },


        metricToggler: {
            padding: theme.spacing(0, 0, 0, 1),
        },
    };
}, { name: 'EntityMetricsValuesViewComp', index: 1 });



/**
 * An interface to keep track of the metrics hidden state, the metrics that have been unselected. Because an entity can have multiple
 * metric definitions and each metric def has numeric/tag metrics, the metrics' hidden states are grouped by metric definition ID.
 */
export interface HiddenMetricsByMetricDefId {
    readonly [metricDefId: string]: {
        readonly [metricName: string]: boolean | undefined;
    } | undefined;
}


export interface EntityMetricsValuesViewCompActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate?: BaseInfoCompSaveAction<HiddenMetricsByMetricDefId>
}


export interface EntityMetricsValuesViewCompProps extends BaseInfoCompProps {
    readonly timestamp: string;

    /**
     * An entity can have more than 1 metric definitions and each metric definition has numeric/tag metrics. We will create 1 field for each
     * numeric/tag metrics and group them by the metric definition ID. Like this
     * fields: {
     *      environment: {
     *          temperature: {label: 'Temperature', value: 45},
     *          pressure: {label: 'Pressure', value: 436},
     *          ambient: {label: 'Ambient Light', value: 123},
     *      },
     *      c3less: {
     *          current: {label: 'Current', value: 31},
     *          voltage: {label: 'Voltage', value: 0.465},
     *      }
     * }
     */
    readonly fields: {
        readonly [metricDefId: string]: {
            readonly [metricName: string]: BaseEntityField<string>;
        } | undefined;
    };

    readonly actions?: EntityMetricsValuesViewCompActionsSet;
    readonly text: {
        readonly updatedDate: string;
    }
    readonly noMetricsMessageComp?: React.ReactNode;
}



/**
 * This is a dumb component used for displaying an entity's set of metrics' values
 */
export const EntityMetricsValuesViewComp: React.FC<EntityMetricsValuesViewCompProps> = (props: EntityMetricsValuesViewCompProps) => {

    // The local state to keep track of the selected metrics. When the user go into edit mode and check/uncheck the metrics, we will
    // update this state.
    const [hiddenMetricsByMetricDefId, setHiddenMetricsByMetricDefId] = useState<HiddenMetricsByMetricDefId | undefined>(undefined);

    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();



    /**
     * Initialize the metric hidden map based on the props.fields' hidden flag.
     */
    const initializeHiddenMetricsMap = useCallback(() => {

        // The props.fields is an object that contains groups of fields for numeric/tag metrics that belong to 1 metric definition.
        // The key for each group is the metric definition ID.
        const metricDefinitionIds = Object.keys(props.fields);

        const hiddenMetricsMap = {};

        metricDefinitionIds.forEach((metricDefId: string) => {
            const metrics = props.fields[metricDefId];
            if (metrics) {
                // Compile the hidden status for each metric/field
                const hiddenMetrics = {};
                Object.entries(metrics).forEach(([metricName, metric]) => {
                    hiddenMetrics[metricName] = Boolean(metric.hidden);
                });

                // If there is at least 1 metric then add the map of hidden metrics to the result
                if (Object.keys(hiddenMetrics).length > 0) {
                    hiddenMetricsMap[metricDefId] = hiddenMetrics;
                }
            }
        });

        if (Object.keys(hiddenMetricsMap).length > 0) {
            setHiddenMetricsByMetricDefId(hiddenMetricsMap);
        } else {
            // If there is NO metrics in props.fields, return undefined
            setHiddenMetricsByMetricDefId(undefined);
        }
    }, [props.fields]);



    // On component init or props.fields changes, initialize the hidden metric maps
    useEffect(() => {
        initializeHiddenMetricsMap();
    }, [initializeHiddenMetricsMap, props.fields]);


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        initializeHiddenMetricsMap();
    }, [initializeHiddenMetricsMap]);



    /**
     * On form submit pass the current hidden metrics local state to the handler
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick(hiddenMetricsByMetricDefId ?? {});
        }

        return false;
    }, [props.actions, hiddenMetricsByMetricDefId]);


    /**
     * Handler for when a metric checkbox is checked/unchecked. When a checkbox is checked, that mean the metric need to be visible so
     * we will set the hidden field for that metric to the opposite, FALSE.
     */
    const onToggleMetricsVisibility = useCallback((metricDefId: string, metricName: string, selected: boolean) => {
        setHiddenMetricsByMetricDefId({
            ...hiddenMetricsByMetricDefId,
            [metricDefId]: {
                ...hiddenMetricsByMetricDefId?.[metricDefId],
                [metricName]: !selected,
            },
        });
    }, [hiddenMetricsByMetricDefId]);


    /**
     * A flag to check if there are any metric configured. If there are no metrics, we will show the noMetricsMessageComp
     */
    const hasMetrics = useMemo(() => {
        // Find a metrics group from the props.field that has at least 1 metric.
        const metricIndex = Object.values(props.fields).findIndex((metrics) => {
            return metrics && Object.keys(metrics).length > 0;
        });

        // There is at least 1 metrics defined in the metric definition's fields so return true
        return metricIndex >= 0;
    }, [props.fields]);


    /**
     * A flag to check if there is any VISIBLE metrics. If there is no visible metrics, we can also show No Metrics message. Even if
     * hasMetrics is true but if hasVisibleMetrics is false, we will also display No Metrics message instead of empty panel
     */
    const hasVisibleMetrics = useMemo(() => {
        // Find a metrics group from the props.field that has at least 1 visible metric.
        const metricIndex = Object.entries(props.fields).findIndex(([metricDefId, metrics]) => {
            if (!metrics) {
                return false;
            }

            // Get the hidden metrics settings for the specified metricDefId
            const hiddenMetricsSettings = hiddenMetricsByMetricDefId?.[metricDefId];

            const hasVisibleMetricsInCurrentGroup = Object.keys(metrics).find((metricName) => {
                // We found 1 metric that is NOT hidden, the value is FALSE or UNDEFINED
                return !hiddenMetricsSettings?.[metricName];
            }) !== undefined;

            return hasVisibleMetricsInCurrentGroup;
        });

        // There is at least 1 metrics defined in the metric definition's fields so return true
        return metricIndex >= 0;
    }, [hiddenMetricsByMetricDefId, props.fields]);


    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                {(props.isEditing || (hiddenMetricsByMetricDefId && hasMetrics && hasVisibleMetrics)) && (
                    <form
                        className={clsx(formClasses.root, props.formProps?.className)}
                        noValidate
                        autoComplete="on"
                        method="POST"
                        onSubmit={onFormSubmit}
                    >
                        <div className={classes.metricsTable}>
                            {Object.entries(props.fields).map(([metricDefId, metrics]) => {

                                // Get the hidden metrics for the specified metricDefId
                                const hiddenMetricsSettings = hiddenMetricsByMetricDefId?.[metricDefId];

                                if (!metrics) {
                                    return <></>;
                                }

                                // Check if there is any visible metric in this group. If there is no visible metrics then we can
                                // hide the metric definition ID display
                                const hasVisibleMetricsInCurrentGroup = Object.keys(metrics).find((metricName) => {
                                    // We found 1 metric that is NOT hidden, the value is FALSE or UNDEFINED
                                    return !hiddenMetricsSettings?.[metricName];
                                }) !== undefined;

                                return (
                                    <div
                                        key={metricDefId}
                                        className={classes.metricsGroup}
                                    >
                                        {/* Don't show metric definition ID if there are not visible metrics, UNLESS we are in Editing mode. */}
                                        {(props.isEditing || hasVisibleMetricsInCurrentGroup) && (
                                            <div className={classes.metricDefinitionId}>
                                                {metricDefId}
                                            </div>
                                        )}

                                        {Object.entries(metrics).map(([metricName, field]) => {
                                            return (
                                                <div
                                                    key={metricName}
                                                    className={clsx(
                                                        classes.metricRow,
                                                        !props.isEditing && hiddenMetricsSettings?.[metricName] && 'hidden',
                                                    )}
                                                >
                                                    <div className={classes.metricNameCol}>{field.label}</div>
                                                    <div className={classes.metricValueCol}>{field.displayValue || field.value}</div>

                                                    {/*
                                                        Show a checkbox for each metric to allow the agent to select/unselect the metrics
                                                        in editing mode
                                                    */}
                                                    {props.isEditing && (
                                                        <Checkbox
                                                            className={classes.metricToggler}
                                                            checked={!hiddenMetricsSettings?.[metricName]}
                                                            value={!hiddenMetricsSettings?.[metricName]}
                                                            color="primary"
                                                            onChange={(event: React.ChangeEvent) => {
                                                                onToggleMetricsVisibility(
                                                                    metricDefId,
                                                                    metricName,
                                                                    (event.target as HTMLInputElement).checked,
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>

                        {props.isEditing && props.actions && (
                            <div className={clsx(formClasses.formActionContainer)}>
                                <div />
                                <div>
                                    {props.actions?.cancelEdit && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            size="small"
                                            startIcon={props.actions.cancelEdit.icon
                                                ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                            onClick={() => {
                                                reverseFormInput();
                                                if (props.actions?.cancelEdit) {
                                                    props.actions.cancelEdit.onClick();
                                                }
                                            }}
                                        >
                                            {props.actions.cancelEdit.label}
                                        </Button>
                                    )}
                                    {props.actions?.saveUpdate && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            size="small"
                                            startIcon={props.actions.saveUpdate.icon
                                                ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                        >
                                            {props.actions.saveUpdate.label}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </form>
                )}

                {(!hasMetrics || !hasVisibleMetrics) && !props.isEditing && props.noMetricsMessageComp}
            </div>

            {!props.isEditing && hasMetrics && (
                <div className={clsx(classes.metricRow, 'timestamp')}>
                    <div className={clsx(classes.metricNameCol)}>{props.text.updatedDate}</div>
                    <div className={clsx(classes.metricValueCol, 'timestamp')}>{props.timestamp}</div>
                </div>
            )}

        </Paper>
    );

};
